import type {
  ReadinessStakeKey,
  SanityReadinessScoringConfig
} from '@lib/sanity/types';
import type {
  ReadinessCaptainOutcome,
  ReadinessTransferOutcome
} from 'types/readiness';

export const DEFAULT_READINESS_SCORING: SanityReadinessScoringConfig = {
  ratingMin: 0,
  ratingMax: 100,
  baseMin: 35,
  baseMax: 70,
  captainMatchPoints: 14,
  captainMismatchPoints: 5,
  transferSelectedPoints: 12,
  transferRollPoints: 4,
  clampMin: 35,
  clampMax: 82,
  targetMates: 80,
  targetWork: 84,
  targetMoney: 88
};

/**
 * Merges a Sanity scoring config over the defaults. The CMS document can
 * be partially filled (missing or null fields), so only numeric values
 * override — anything else falls back to DEFAULT_READINESS_SCORING.
 */
export const resolveScoringConfig = (
  config: Partial<SanityReadinessScoringConfig> | null | undefined
): SanityReadinessScoringConfig => {
  if (!config) return DEFAULT_READINESS_SCORING;

  const merged = { ...DEFAULT_READINESS_SCORING };
  for (const key of Object.keys(
    DEFAULT_READINESS_SCORING
  ) as (keyof SanityReadinessScoringConfig)[]) {
    const value = config[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      merged[key] = value;
    }
  }

  return merged;
};

export type ReadinessScoreInput = {
  squadRating: number | null;
  captainOutcome: ReadinessCaptainOutcome;
  transferOutcome: ReadinessTransferOutcome;
  stakes: ReadinessStakeKey | null;
};

export type ReadinessScore = {
  score: number;
  winnersTarget: number;
  gap: number;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

export const getWinnersTarget = (
  stakes: ReadinessStakeKey | null,
  config: SanityReadinessScoringConfig
): number => {
  switch (stakes) {
    case 'work':
      return config.targetWork;
    case 'money':
      return config.targetMoney;
    default:
      return config.targetMates;
  }
};

/**
 * Deterministic pre-GW1 readiness score (spec 6.3). Base maps the squad
 * rating into [baseMin, baseMax]; captain and transfer inputs add fixed
 * points. The score is clamped so it can never reach the winners target.
 */
export const calculateReadinessScore = (
  input: ReadinessScoreInput,
  config: SanityReadinessScoringConfig = DEFAULT_READINESS_SCORING
): ReadinessScore => {
  const ratingRange = config.ratingMax - config.ratingMin;
  const normalisedRating =
    ratingRange > 0
      ? clamp(((input.squadRating ?? 0) - config.ratingMin) / ratingRange, 0, 1)
      : 0;
  const base =
    config.baseMin + normalisedRating * (config.baseMax - config.baseMin);

  const captainPoints =
    input.captainOutcome === 'match'
      ? config.captainMatchPoints
      : input.captainOutcome === 'mismatch'
      ? config.captainMismatchPoints
      : 0;

  const transferPoints =
    input.transferOutcome === 'selected'
      ? config.transferSelectedPoints
      : input.transferOutcome === 'roll'
      ? config.transferRollPoints
      : 0;

  const winnersTarget = getWinnersTarget(input.stakes, config);

  // The score must never reach or exceed the winners target, so the
  // configured ceiling is tightened to target - 1 where necessary.
  const ceiling = Math.min(config.clampMax, winnersTarget - 1);
  const score = Math.round(
    clamp(base + captainPoints + transferPoints, config.clampMin, ceiling)
  );

  return { score, winnersTarget, gap: winnersTarget - score };
};

/**
 * Resolves a gap to its odds band index. Bands are matched on inclusive
 * [minGap, maxGap]; gaps beyond every band fall into the nearest one.
 */
export const resolveOddsBandIndex = (
  gap: number,
  bands: { minGap: number; maxGap: number }[]
): number => {
  if (bands.length === 0) return -1;

  const indexed = bands
    .map((band, index) => ({ ...band, index }))
    .sort((a, b) => a.minGap - b.minGap);

  const match = indexed.find(
    (band) => gap >= band.minGap && gap <= band.maxGap
  );
  if (match) return match.index;

  if (gap < indexed[0].minGap) return indexed[0].index;
  return indexed[indexed.length - 1].index;
};
